import React from 'react';
import {
  Container, Typography, Grid, Card, CardContent, CardMedia, Button,
  Pagination, Box, Chip, CircularProgress, Alert, Tabs, Tab, Paper,
  FormControl, InputLabel, Select, MenuItem, TextField, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Popover,
  Checkbox, FormControlLabel,
} from '@mui/material';
import {
  KeyboardArrowDown, ViewList, ExpandMore, OpenInNew, ArrowUpward,
  ArrowDownward, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import BlogDialog from '../../../components/BlogDialog';
import { updateBlogPost } from '../../../services/blogService';
import { useBlogContext } from '../../BlogContext';
import { mapIdToShortString, getDaysInMonth, getFirstDayOfMonth, formatDateDisplay, formatDateRange, formatDateForInput, isSameDay, isDateInRange } from '../blogUtils';

const MergedBlogsTab = () => {
  const {
    isAdmin, posts, page, totalPages, loading, error, selectedTag,
    setSelectedTag, dialogOpen, setDialogOpen, dialogMode, selectedPost,
    tabValue, setTabValue, categories, preBuiltCategory, preBuiltSubcategory,
    rankingCriteria, setRankingCriteria, preBuiltLoading, mergedBlogs,
    loadingMergedBlogs, blogPosts, blogPerformanceData, mergedBlogPerformanceData,
    blogPageStats, sourceBlogMap, pausedBlogs, expandedStatsDialog,
    setExpandedStatsDialog, mergedBlogsLimit, setMergedBlogsLimit,
    blogsPerMergedBlogLimit, setBlogsPerMergedBlogLimit,
    mergedBlogsStartDate, mergedBlogsEndDate,
    dateRangePickerOpen, setDateRangePickerOpen,
    dateRangePickerAnchor, setDateRangePickerAnchor,
    tempStartDate, setTempStartDate, tempEndDate, setTempEndDate,
    startMonth, setStartMonth, endMonth, setEndMonth,
    includeToday, setIncludeToday, snackbar, setSnackbar,
    handleCloseSnackbar, handlePageChange, handleTagClick,
    handleCreatePost, handleViewPost, handleEditPost,
    handleCloseDialog, handleDialogSuccess,
    handlePreBuiltCategoryChange, handlePreBuiltSubcategoryChange,
    fetchMergedBlogs, handleReorderBlogsInMerged, handleRemoveBlogFromMerged,
    isAuthenticated, user,
    showSnackbar, reloadMergedBlog,
    setPausedBlogs,
    setMergedBlogsStartDate, setMergedBlogsEndDate,
    allTags, blogActions,
  } = useBlogContext();

  return (
    <Grid container spacing={3}>
      {/* Manual snake creation section hidden - snakes are now auto-generated */}
    
      {/* Merged Blogs Management Section */}
      <Grid item xs={12} sx={{ mt: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">
              Blog Optimizer
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Date Range Filter - Calendar Picker */}
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  setDateRangePickerAnchor(e.currentTarget);
                  setDateRangePickerOpen(true);
                  setTempStartDate(mergedBlogsStartDate || null);
                  setTempEndDate(mergedBlogsEndDate || null);
                  if (mergedBlogsStartDate) {
                    setStartMonth(new Date(mergedBlogsStartDate));
                  }
                  if (mergedBlogsEndDate) {
                    setEndMonth(new Date(mergedBlogsEndDate));
                  }
                }}
                endIcon={<KeyboardArrowDown />}
                sx={{ minWidth: 250, justifyContent: 'space-between' }}
              >
                {formatDateRange(mergedBlogsStartDate, mergedBlogsEndDate)}
              </Button>
    
              {(mergedBlogsStartDate || mergedBlogsEndDate) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setMergedBlogsStartDate('');
                    setMergedBlogsEndDate('');
                  }}
                >
                  Clear
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={fetchMergedBlogs}
                disabled={loadingMergedBlogs}
                startIcon={loadingMergedBlogs ? <CircularProgress size={16} /> : <ViewList />}
              >
                Refresh
              </Button>
            </Box>
          </Box>
    
          {loadingMergedBlogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (() => {
            // Filter merged blogs by date range
            let filteredMergedBlogs = mergedBlogs;
    
            if (mergedBlogsStartDate || mergedBlogsEndDate) {
              filteredMergedBlogs = mergedBlogs.filter(blog => {
                const blogDateObj = new Date(blog.createdAt || blog._id);
                const blogDateStr = formatDateForInput(blogDateObj);
    
                if (mergedBlogsStartDate && mergedBlogsEndDate) {
                  return blogDateStr >= mergedBlogsStartDate && blogDateStr <= mergedBlogsEndDate;
                } else if (mergedBlogsStartDate) {
                  return blogDateStr >= mergedBlogsStartDate;
                } else if (mergedBlogsEndDate) {
                  return blogDateStr <= mergedBlogsEndDate;
                }
                return true;
              });
            }
    
            return filteredMergedBlogs.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {mergedBlogsStartDate || mergedBlogsEndDate
                  ? 'No snakes found for the selected date range.'
                  : 'No snakes found. Generate a snake to create one.'}
              </Alert>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: 2,
                  pb: 2,
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#555',
                    },
                  },
                }}
              >
                {filteredMergedBlogs.slice(0, mergedBlogsLimit).map((mergedBlog) => {
                  const mergedBlogIdStr = mergedBlog._id?.toString();
                  const sourceBlogIds = mergedBlog.sourceBlogIds || [];
                  const blogsLimit = blogsPerMergedBlogLimit[mergedBlogIdStr] || 5;
                  const displayedBlogs = sourceBlogIds.slice(0, blogsLimit);
                  const hasMoreBlogs = sourceBlogIds.length > blogsLimit;
                  const perfData = mergedBlogPerformanceData[mergedBlog._id];
    
                  // Get category and subcategory names
                  let categoryId = null;
                  let subcategoryId = null;
    
                  if (mergedBlog.categoryId) {
                    categoryId = typeof mergedBlog.categoryId === 'object' && mergedBlog.categoryId !== null
                      ? (mergedBlog.categoryId._id || mergedBlog.categoryId)
                      : mergedBlog.categoryId;
                  }
    
                  if (mergedBlog.subcategoryId) {
                    subcategoryId = typeof mergedBlog.subcategoryId === 'object' && mergedBlog.subcategoryId !== null
                      ? (mergedBlog.subcategoryId._id || mergedBlog.subcategoryId)
                      : mergedBlog.subcategoryId;
                  }
    
                  // If merged blog doesn't have category/subcategory, try to get from first source blog
                  if (!categoryId && sourceBlogIds.length > 0) {
                    const firstSourceBlogId = sourceBlogIds[0];
                    const firstSourceBlogIdStr = typeof firstSourceBlogId === 'object' && firstSourceBlogId !== null
                      ? (firstSourceBlogId._id || firstSourceBlogId)?.toString()
                      : firstSourceBlogId?.toString();
    
                    if (firstSourceBlogIdStr) {
                      const firstSourceBlog = blogPosts.find(bp => {
                        const bpId = bp?._id?.toString();
                        return bpId === firstSourceBlogIdStr;
                      });
    
                      if (firstSourceBlog) {
                        if (firstSourceBlog.categoryId) {
                          categoryId = typeof firstSourceBlog.categoryId === 'object' && firstSourceBlog.categoryId !== null
                            ? (firstSourceBlog.categoryId._id || firstSourceBlog.categoryId)
                            : firstSourceBlog.categoryId;
                        }
                        if (firstSourceBlog.subcategoryId) {
                          subcategoryId = typeof firstSourceBlog.subcategoryId === 'object' && firstSourceBlog.subcategoryId !== null
                            ? (firstSourceBlog.subcategoryId._id || firstSourceBlog.subcategoryId)
                            : firstSourceBlog.subcategoryId;
                        }
                      }
                    }
                  }
    
                  const category = categoryId ? categories.find(c => {
                    if (!c || !c._id) return false;
                    const catId = typeof c._id === 'object' && c._id !== null
                      ? (c._id._id || c._id)
                      : c._id;
                    return catId?.toString() === categoryId?.toString();
                  }) : null;
    
                  const subcategory = category && subcategoryId ? category.subcategories?.find(s => {
                    if (!s || !s._id) return false;
                    const subId = typeof s._id === 'object' && s._id !== null
                      ? (s._id._id || s._id)
                      : s._id;
                    return subId?.toString() === subcategoryId?.toString();
                  }) : null;
    
                  // Calculate metrics
                  const views = perfData?.count || 0;
                  const clicks = perfData?.clicks || 0;
                  const sales = perfData?.sales || 0;
                  const revenue = perfData?.totalRevenue || perfData?.revenue || 0;
                  const ctr = clicks > 0 ? ((clicks / views) * 100).toFixed(2) : '0.00';
                  const cvr = clicks > 0 ? ((sales / clicks) * 100).toFixed(2) : '0.00';
                  const rpv = views > 0 ? (revenue / views).toFixed(2) : '0.00';
    
                  return (
                    <Card
                      key={mergedBlog._id}
                      sx={{
                        minWidth: 400,
                        maxWidth: 400,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1.5 }}>
                        <Typography variant="h6" noWrap sx={{ mb: 0 }}>
                          {mergedBlog.title}
                        </Typography>
    
                        {/* Performance Metrics Section */}
                        <Box sx={{ mt: 0, pt: 0, borderTop: '1px solid #e0e0e0' }}>
                          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { border: '1px solid #e0e0e0', padding: '4px 6px', textAlign: 'center', fontSize: '0.7rem' } }}>
                            <TableBody>
                              {/* Category and Sub Category - 2 columns in 2 rows */}
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem', padding: '4px 6px' }}>Category</TableCell>
                                <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem', padding: '4px 6px' }}>Sub Category</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontSize: '0.7rem', padding: '4px 6px' }}>{category?.name || 'N/A'}</TableCell>
                                <TableCell colSpan={2} sx={{ fontSize: '0.7rem', padding: '4px 6px' }}>{subcategory?.name || 'N/A'}</TableCell>
                              </TableRow>
    
                              {/* Engagement Metrics - 3 columns in 2 rows, spanning full width */}
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>Views</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>Clicks</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>CTR</TableCell>
                                <TableCell sx={{ padding: 0, border: 'none', width: 0, minWidth: 0, maxWidth: 0 }}></TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{views.toLocaleString()}</TableCell>
                                <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{clicks.toLocaleString()}</TableCell>
                                <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{ctr}%</TableCell>
                                <TableCell sx={{ padding: 0, border: 'none', width: 0, minWidth: 0, maxWidth: 0 }}></TableCell>
                              </TableRow>
    
                              {/* Conversion and Revenue Metrics - 4 columns in 2 rows */}
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>Sales</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>CVR</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>Revenue</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>RPV</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>{sales.toLocaleString()}</TableCell>
                                <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>{cvr}%</TableCell>
                                <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>${revenue.toFixed(2)}</TableCell>
                                <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>${rpv}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
    
                        {/* Containing Blogs List - Always Visible */}
                        <Box sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: '1px solid #e0e0e0',
                          maxHeight: 400,
                          overflowY: 'auto',
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '3px',
                            '&:hover': {
                              background: 'rgba(0, 0, 0, 0.3)',
                            },
                          },
                          scrollbarWidth: 'thin',
                          scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                        }}>
                          {sourceBlogIds.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No blogs in this snake.
                            </Typography>
                          ) : (
                            <Box>
                              {displayedBlogs.map((sourceBlogId, index) => {
                                const blogId = typeof sourceBlogId === 'object' ? sourceBlogId._id || sourceBlogId : sourceBlogId;
                                const blogIdStr = blogId?.toString();
                                const perfData = blogIdStr ? blogPerformanceData[blogIdStr] : null;
    
                                const sourceBlog = blogPosts.find(bp => {
                                  const bpId = bp._id?.toString();
                                  return bpId === blogIdStr;
                                });
    
                                const blogThumbnail = sourceBlog?.featuredImage || null;
    
                                let contentImage = null;
                                if (!blogThumbnail && sourceBlog?.content) {
                                  try {
                                    let content;
                                    if (typeof sourceBlog.content === 'string') {
                                      // Only try to parse if it looks like JSON (starts with [ or {)
                                      const trimmed = sourceBlog.content.trim();
                                      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                                        content = JSON.parse(sourceBlog.content);
                                      } else {
                                        // Not JSON, skip parsing
                                        content = null;
                                      }
                                    } else {
                                      content = sourceBlog.content;
                                    }
    
                                    if (content && Array.isArray(content)) {
                                      for (const page of content) {
                                        if (page.elements && Array.isArray(page.elements)) {
                                          const mediaElement = page.elements.find(el =>
                                            el.type === 'media' && el.mediaUrl
                                          );
                                          if (mediaElement) {
                                            contentImage = mediaElement.mediaUrl;
                                            break;
                                          }
                                        }
                                      }
                                    }
                                  } catch (e) {
                                    // Silently ignore parse errors - content may not be JSON
                                  }
                                }
    
                                const thumbnailUrl = blogThumbnail || contentImage;
    
                                const affiliateId = sourceBlog?.author?._id || sourceBlog?.author || 'N/A';
                                // Prefer the permanent, sequential displayId; fall back to the old
                                // hash-based short string for legacy users that predate it.
                                const affiliateDisplayId = sourceBlog?.author?.displayId != null
                                  ? sourceBlog.author.displayId
                                  : mapIdToShortString(affiliateId);
                                const affiliateName = sourceBlog?.author?.name || sourceBlog?.author?.email || 'N/A';
                                const affiliateUsername = sourceBlog?.author?.username || 'N/A';
    
                                const isPaused = pausedBlogs[mergedBlogIdStr]?.has(blogIdStr) || false;
    
                                return (
                                  <Card
                                    key={`${mergedBlogIdStr}-${blogIdStr || index}`}
                                    variant="outlined"
                                    sx={{
                                      mb: 1,
                                      width: '100%',
                                      overflow: 'visible',
                                      opacity: isPaused ? 0.5 : 1,
                                      backgroundColor: isPaused ? '#f5f5f5' : 'transparent',
                                      '&:hover': { backgroundColor: isPaused ? '#f5f5f5' : '#fafafa' }
                                    }}
                                  >
                                    {/* Affiliate and Blog Info Table */}
                                    <Box sx={{ p: 0, borderBottom: '1px solid #e0e0e0' }}>
                                      <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { border: '1px solid #e0e0e0', padding: '4px 6px', fontSize: '0.7rem' } }}>
                                        <TableBody>
                                          {/* Affiliate Information */}
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Affiliate ID</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Affiliate Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Username</TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell sx={{ width: '33.33%', fontSize: '0.7rem' }}>{affiliateDisplayId}</TableCell>
                                            <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={affiliateName}>{affiliateName}</TableCell>
                                            <TableCell sx={{ width: '33.33%', fontSize: '0.7rem' }}>{affiliateUsername}</TableCell>
                                          </TableRow>
    
                                          {/* Blog Information */}
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem' }}>Blog ID</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem' }} colSpan={2}>Blog Name</TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.7rem' }}>{mapIdToShortString(blogIdStr)}</TableCell>
                                            <TableCell colSpan={2} sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis' }} title={sourceBlog?.title || `Blog ${index + 1}`}>{sourceBlog?.title || `Blog ${index + 1}`}</TableCell>
                                          </TableRow>
                                        </TableBody>
                                      </Table>
                                    </Box>
    
                                    {/* Blog Preview and Stats Layout */}
                                    <Box sx={{ p: 1.5 }}>
                                      {(() => {
                                        let firstPageElements = [];
                                        let blogToUse = sourceBlog;
    
                                        if (!blogToUse || !blogToUse.content) {
                                          blogToUse = sourceBlogMap[blogIdStr] || null;
                                        }
    
                                        if (!blogToUse || !blogToUse.content) {
                                          const foundBlog = blogPosts.find(bp => {
                                            const bpId = bp._id?.toString();
                                            return bpId === blogIdStr;
                                          });
                                          if (foundBlog && foundBlog.content) {
                                            blogToUse = foundBlog;
                                          }
                                        }
    
                                        if (blogToUse?.content) {
                                          try {
                                            let content;
                                            if (typeof blogToUse.content === 'string') {
                                              // Only try to parse if it looks like JSON (starts with [ or {)
                                              const trimmed = blogToUse.content.trim();
                                              if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                                                content = JSON.parse(blogToUse.content);
                                              } else {
                                                // Not JSON, skip parsing
                                                content = null;
                                              }
                                            } else {
                                              content = blogToUse.content;
                                            }
    
                                            if (content && Array.isArray(content) && content.length > 0) {
                                              const firstItem = content[0];
    
                                              if (firstItem && firstItem.elements && Array.isArray(firstItem.elements)) {
                                                firstPageElements = firstItem.elements;
                                              } else if (firstItem && firstItem.type) {
                                                firstPageElements = content.slice(0, 5);
                                              } else if (Array.isArray(firstItem)) {
                                                firstPageElements = firstItem;
                                              }
                                            }
                                          } catch (e) {
                                            // Silently skip if content is not valid JSON
                                            // This is expected for some blog formats
                                          }
                                        }
    
                                        const views = perfData?.count || perfData?.totalViews || 0;
                                        const clicks = perfData?.clicks || 0;
                                        const sales = perfData?.sales || 0;
                                        const revenue = perfData?.revenue || perfData?.totalRevenue || 0;
    
                                        const pageStats = blogPageStats[blogIdStr] || [];
                                        const offerStatsMap = new Map();
    
                                        pageStats.forEach(page => {
                                          if (page.offerId && page.offerName) {
                                            const offerIdStr = page.offerId.toString();
                                            if (!offerStatsMap.has(offerIdStr)) {
                                              offerStatsMap.set(offerIdStr, {
                                                offerId: offerIdStr,
                                                offerName: page.offerName,
                                                views: 0,
                                                clicks: 0,
                                                sales: 0,
                                                revenue: 0
                                              });
                                            }
                                            const offerStat = offerStatsMap.get(offerIdStr);
                                            offerStat.views += page.views || 0;
                                            offerStat.clicks += page.clicks || 0;
                                            offerStat.sales += page.sales || 0;
                                            offerStat.revenue += page.revenue || page.totalRevenue || 0;
                                          }
                                        });
    
                                        const offerStats = Array.from(offerStatsMap.values()).map(offer => {
                                          const ctr = offer.views > 0 ? ((offer.clicks / offer.views) * 100).toFixed(2) : '0.00';
                                          const cvr = offer.clicks > 0 ? ((offer.sales / offer.clicks) * 100).toFixed(2) : '0.00';
                                          const rpv = offer.views > 0 ? (offer.revenue / offer.views).toFixed(2) : '0.00';
                                          return {
                                            ...offer,
                                            ctr,
                                            cvr,
                                            rpv
                                          };
                                        });
    
                                        return (
                                          <Box>
                                            <Box sx={{
                                              display: 'flex',
                                              gap: 1,
                                              mt: 1,
                                              width: '100%',
                                              overflowX: 'hidden',
                                              overflowY: 'visible'
                                            }}>
                                              {/* Left: First Page Preview - Video or Image in square frame */}
                                              {(() => {
                                                const cardWidth = 400;
                                                const previewWidth = Math.round(cardWidth / 4);
                                                const previewHeight = previewWidth;
    
                                                const mediaElement = firstPageElements.find(el =>
                                                  el.type === 'media' && el.mediaUrl
                                                );
    
                                                const isVideo = mediaElement?.mediaUrl?.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i);
    
                                                return (
                                                  <Box sx={{
                                                    width: `${previewWidth}px`,
                                                    minWidth: `${previewWidth}px`,
                                                    flexShrink: 0,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                    p: 0,
                                                    backgroundColor: '#000',
                                                    height: `${previewHeight}px`,
                                                    maxHeight: `${previewHeight}px`,
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                  }}>
                                                    {mediaElement ? (
                                                      isVideo ? (
                                                        <video
                                                          src={mediaElement.mediaUrl}
                                                          style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                          }}
                                                          controls
                                                        />
                                                      ) : (
                                                        <img
                                                          src={mediaElement.mediaUrl}
                                                          alt="Blog media"
                                                          style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                          }}
                                                        />
                                                      )
                                                    ) : (
                                                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontSize: '0.6rem', color: '#fff' }}>
                                                        No media available
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                );
                                              })()}
    
                                              {/* Right: Performance Stats Table */}
                                              <Box sx={{
                                                flex: 1,
                                                overflowX: 'hidden',
                                                overflowY: 'visible',
                                                maxWidth: 'calc(100% - 110px)'
                                              }}>
                                                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { fontSize: '0.5rem', padding: '3px 2px', textAlign: 'center' } }}>
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '20%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Offer</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Views</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Clicks</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '12%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>CTR</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Sales</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '12%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>CVR</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '11%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Rev</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    <TableRow>
                                                      <TableCell sx={{ fontWeight: 'medium', fontSize: '0.5rem', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>content</TableCell>
                                                      <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{views.toLocaleString()}</TableCell>
                                                      <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                      <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                      <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                      <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                      <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                    </TableRow>
                                                    {offerStats.slice(0, 2).map((offer, offerIndex) => (
                                                      <TableRow key={offerIndex}>
                                                        <TableCell sx={{ fontWeight: 'medium', fontSize: '0.5rem', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, textAlign: 'center' }} title={offer.offerName}>{offer.offerName}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.views.toLocaleString()}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.clicks.toLocaleString()}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.ctr}%</TableCell>
                                                        <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.sales.toLocaleString()}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.cvr}%</TableCell>
                                                        <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>${offer.revenue.toFixed(2)}</TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                  <Button
                                                    size="small"
                                                    onClick={() => {
                                                      setExpandedStatsDialog({
                                                        blogId: blogIdStr,
                                                        blogTitle: blogToUse?.title || sourceBlog?.title || `Blog ${index + 1}`,
                                                        offerStats,
                                                        views,
                                                        clicks,
                                                        sales,
                                                        revenue
                                                      });
                                                    }}
                                                    endIcon={<OpenInNew />}
                                                    sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                                                  >
                                                    View Full Stats
                                                  </Button>
                                                </Box>
    
                                                {/* Pause, Remove, and Up/Down Buttons */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                                                  {/* Pause and Remove Buttons */}
                                                  {/* Pause and Remove Buttons */}
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    onClick={async () => {
                                                      const mergedBlogIdStr = mergedBlog._id?.toString();
                                                      const isPaused = pausedBlogs[mergedBlogIdStr]?.has(blogIdStr) || false;
    
                                                      setPausedBlogs(prev => {
                                                        const newState = { ...prev };
                                                        if (!newState[mergedBlogIdStr]) {
                                                          newState[mergedBlogIdStr] = new Set();
                                                        }
    
                                                        if (isPaused) {
                                                          newState[mergedBlogIdStr] = new Set(newState[mergedBlogIdStr]);
                                                          newState[mergedBlogIdStr].delete(blogIdStr);
                                                        } else {
                                                          newState[mergedBlogIdStr] = new Set(newState[mergedBlogIdStr]);
                                                          newState[mergedBlogIdStr].add(blogIdStr);
                                                        }
    
                                                        return newState;
                                                      });
    
                                                      try {
                                                        const mergedBlog = mergedBlogs.find(mb => mb._id?.toString() === mergedBlogIdStr);
                                                        if (!mergedBlog) {
                                                          showSnackbar('Snake not found', 'error');
                                                          return;
                                                        }
    
                                                        const currentPausedSet = pausedBlogs[mergedBlogIdStr] || new Set();
                                                        const newPausedSet = new Set(currentPausedSet);
    
                                                        if (isPaused) {
                                                          newPausedSet.delete(blogIdStr);
                                                        } else {
                                                          newPausedSet.add(blogIdStr);
                                                        }
    
                                                        const pausedBlogIdsArray = Array.from(newPausedSet);
    
                                                        await updateBlogPost(mergedBlogIdStr, {
                                                          pausedBlogIds: pausedBlogIdsArray
                                                        });
    
                                                        showSnackbar(isPaused ? 'Blog resumed' : 'Blog paused', 'success');
    
                                                        await reloadMergedBlog(mergedBlogIdStr);
                                                      } catch (error) {
                                                        console.error('Error updating paused state:', error);
                                                        showSnackbar('Failed to update paused state', 'error');
                                                        setPausedBlogs(prev => {
                                                          const revertedState = { ...prev };
                                                          if (!revertedState[mergedBlogIdStr]) {
                                                            revertedState[mergedBlogIdStr] = new Set();
                                                          }
                                                          if (isPaused) {
                                                            revertedState[mergedBlogIdStr] = new Set(revertedState[mergedBlogIdStr]);
                                                            revertedState[mergedBlogIdStr].add(blogIdStr);
                                                          } else {
                                                            revertedState[mergedBlogIdStr] = new Set(revertedState[mergedBlogIdStr]);
                                                            revertedState[mergedBlogIdStr].delete(blogIdStr);
                                                          }
                                                          return revertedState;
                                                        });
                                                      }
                                                    }}
                                                    sx={{
                                                      textTransform: 'none',
                                                      minWidth: '70px',
                                                      fontSize: '0.7rem',
                                                      backgroundColor: pausedBlogs[mergedBlog._id?.toString()]?.has(blogIdStr) ? 'warning.light' : 'transparent',
                                                      borderColor: 'warning.main',
                                                      color: 'warning.main',
                                                      '&:hover': {
                                                        borderColor: 'warning.dark',
                                                        backgroundColor: 'warning.light',
                                                        color: 'warning.dark'
                                                      }
                                                    }}
                                                  >
                                                    {pausedBlogs[mergedBlog._id?.toString()]?.has(blogIdStr) ? 'Resume' : 'Pause'}
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleRemoveBlogFromMerged(mergedBlog._id, sourceBlogId)}
                                                    sx={{
                                                      textTransform: 'none',
                                                      minWidth: '70px',
                                                      fontSize: '0.7rem',
                                                      borderColor: 'error.main',
                                                      color: 'error.main',
                                                      '&:hover': {
                                                        borderColor: 'error.dark',
                                                        backgroundColor: 'error.light',
                                                        color: 'error.dark'
                                                      }
                                                    }}
                                                  >
                                                    Remove
                                                  </Button>
    
                                                  {/* Up/Down Arrows */}
                                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      disabled={index === 0}
                                                      onClick={async () => {
                                                        if (index > 0) {
                                                          const newOrder = [...sourceBlogIds];
                                                          [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                                                          await handleReorderBlogsInMerged(mergedBlog._id, newOrder);
                                                        }
                                                      }}
                                                      sx={{ p: 0.5 }}
                                                    >
                                                      <ArrowUpward fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      disabled={index === sourceBlogIds.length - 1}
                                                      onClick={async () => {
                                                        if (index < sourceBlogIds.length - 1) {
                                                          const newOrder = [...sourceBlogIds];
                                                          [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                                                          await handleReorderBlogsInMerged(mergedBlog._id, newOrder);
                                                        }
                                                      }}
                                                      sx={{ p: 0.5 }}
                                                    >
                                                      <ArrowDownward fontSize="small" />
                                                    </IconButton>
                                                  </Box>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Box>
                                        );
                                      })()}
                                    </Box>
                                  </Card>
                                );
                              })}
    
                              {/* Load More Blogs Button */}
                              {hasMoreBlogs && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setBlogsPerMergedBlogLimit(prev => ({
                                        ...prev,
                                        [mergedBlogIdStr]: (prev[mergedBlogIdStr] || 5) + 5
                                      }));
                                    }}
                                    endIcon={<ExpandMore />}
                                    sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                                  >
                                    Load {Math.min(5, sourceBlogIds.length - blogsLimit)} More Blogs
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
    
                {/* Load More Merged Blogs Button */}
                {filteredMergedBlogs.length > mergedBlogsLimit && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setMergedBlogsLimit(prev => prev + 5);
                      }}
                      endIcon={<ExpandMore />}
                      sx={{ textTransform: 'none' }}
                    >
                      Load {Math.min(5, filteredMergedBlogs.length - mergedBlogsLimit)} More Snakes
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default MergedBlogsTab;
